/**
 * Minimal agent startup — registers only the services needed for
 * AI inspection tools (hierarchy, properties, AI tools).
 *
 * Does NOT import editor.common.ts (UI commands) or create any renderers.
 * Visual inspector features (InspectorView, ThreeEditor, KTX2/DRACO loaders)
 * are excluded from this entry point.
 */

import { Application, IApplication } from "../services/application/common/application.js";
import { toLocalISOString } from "../../base/common/date.js";
import { URI } from "../../base/common/uri.js";
import { IAIContextService, IAIService, IAIToolService } from "../../platform/ai/common/ai.js";
import { AIContextService } from "../../platform/ai/common/aiContext.js";
import { AIService } from "../../platform/ai/common/aiService.js";
import { AIHierarchyContext } from "../../platform/ai/common/context/hierarchyContext.js";
import { AIToolService } from "../../platform/ai/common/aiToolService.js";
import { FindPropertiesTool, ReadPropertyTool } from "../../platform/ai/common/tools/propertyTools.js";
import { LocalStorageFileSystemProvider } from "../../platform/files/browser/localStorageFileSystemProvider.js";
import { IFileService } from "../../platform/files/common/files.js";
import { FileService } from "../../platform/files/common/fileService.js";
import { SyncDescriptor } from "../../platform/instantiation/common/descriptors.js";
import { IInstantiationService } from "../../platform/instantiation/common/instantiation.js";
import { InstantiationService } from "../../platform/instantiation/common/instantiationService.js";
import { getSingletonServiceDescriptors } from "../../platform/instantiation/common/registry.js";
import { ServiceCollection } from "../../platform/instantiation/common/serviceCollection.js";
import { BufferLogger } from "../../platform/log/common/bufferLog.js";
import { FileLoggerService } from "../../platform/log/common/fileLog.js";
import { ConsoleLogger, ILogger, ILoggerService, ILogService, LogLevel } from "../../platform/log/common/log.js";
import { LogService } from "../../platform/log/common/logService.js";
import { IWebGLService } from "../../platform/webgl/common/webgl.js";
import { WebGLService } from "../../platform/webgl/common/webglService.js";
import { ThreeTreeProvider } from "../../platform/webgl/three.js/treeProvider.js";
import { ThreePropertyProvider } from "../../platform/webgl/three.js/propertyProvider.js";
import { IPropertyAccessorService } from "../../platform/properties/common/accessor.js";
import { IProjectService } from "../services/project/common/project.js";
import { BasicProjectService } from "../services/project/browser/projectService.js";
import { IPropertyService } from "../../platform/properties/common/properties.js";
import { PropertyService } from "../../platform/properties/common/propertyService.js";
import { PropertyAccessorService } from "../../platform/properties/common/accessorService.js";
import { IHierarchyService, ITreeProviderService } from "../../platform/hierarchy/common/hierarchy.js";
import { HierarchyService } from "../../platform/hierarchy/common/hierarchyService.js";
import { TreeProviderService } from "../../platform/hierarchy/common/treeProvider.js";
import { IStorageService } from "../../platform/storage/common/storage.js";
import { BrowserStorageService } from "../services/storage/browser/storageService.js";
import { ConfigurationService } from "../../platform/configuration/common/configurationService.js";
import { IConfigurationService } from "../../platform/configuration/common/configuration.js";
import { ContextKeyService } from "../../platform/contextkey/browser/contextKeyService.js";
import { IContextKeyService } from "../../platform/contextkey/common/contextkey.js";
import { IProductService, ProductService } from "../../platform/product/common/productService.js";
import { product } from "../../platform/product/common/product.js";
import { IUserConfiguration } from "../services/user-configuration/common/userConfiguration.js";
import { UserConfiguration } from "../services/user-configuration/common/userConfiguration.impl.js";
import { onUnexpectedError } from "../../base/common/errors.js";
import { IUndoRedoService } from "../../platform/undo/common/undo.js";
import { UndoRedoService } from "../../platform/undo/common/undoService.js";
import { INotificationService } from "../../platform/notifications/common/notifications.js";
import { NotificationService } from "../../platform/notifications/common/notificationService.js";
import { IMenuService } from "../../platform/actions/common/actions.js";
import { MenuService } from "../../platform/actions/common/menuService.js";
import { IContextMenuService } from "../../platform/contextview/common/context.js";
import { ContextMenuService } from "../../platform/contextview/common/contextMenuService.js";
import { IAnalyticsService } from "../../platform/analytics/common/analytics.js";
import { RybbitAnalyticsService } from "../../platform/analytics/browser/RybbitAnalyticsService.js";
import { IIdentitiyService } from "../../platform/auth/common/auth.js";
import { IWindowsMainService } from "../../platform/windows/common/windows.js";
import { WindowsMainService } from "../../platform/windows/browser/windowsMainService.js";
import { IKeyboardService } from "../../platform/keyboard/common/keyboardService.js";
import { BrowserKeyboardService } from "../../platform/keyboard/browser/keyboardService.js";
import { IPointerService } from "../../platform/pointer/common/pointer.js";
import { BrowserPointerService } from "../../platform/pointer/browser/pointerService.js";
import { IResourceService } from "../../platform/resources/common/resources.js";
import { ResourceService } from "../../platform/resources/common/resourcesService.js";
import { IAnyWorkspaceIdentifier } from "../../platform/workspace/common/workspace.js";


class NoopIdentityService implements IIdentitiyService {
    readonly _serviceBrand: undefined;
    get isLoggedIn() { return false; }
    get userId() { return null; }
    get onDidChange() { return { dispose() { } } as any; }
    login() { return Promise.resolve(); }
    logout() { return Promise.resolve(); }
}


export function startup(): Promise<IApplication> {
    return new Promise(async resolve => {
        const [instantiationService, _services, userConfiguration] = await registerServices();

        instantiationService.invokeFunction(async accessor => {
            const application = instantiationService.createInstance<IApplication>(new SyncDescriptor(Application, [false]));

            // Register hierarchy context for AI tools
            const context = accessor.get(IAIContextService);
            if (context) {
                const appContext = instantiationService.createInstance<AIHierarchyContext>(new SyncDescriptor(AIHierarchyContext));
                context.registerProvider(appContext);
            }

            // Register the Three.js tree provider so the hierarchy service can
            // walk Three.js scene graphs. We do NOT register the full ThreeJs engine
            // (which hooks scene.onBeforeRender and conflicts with the host engine).
            const treeProviderService = accessor.get(ITreeProviderService);
            if (treeProviderService) {
                treeProviderService.registerProvider(
                    instantiationService.createInstance<ThreeTreeProvider>(new SyncDescriptor(ThreeTreeProvider))
                );
            }

            // Register ThreePropertyProvider so property_find / property_read
            // tools can inspect Object3D properties. The full ThreeJs engine is
            // NOT registered (it hooks onBeforeRender and crashes the host).
            // Instead we pass a lightweight shim that only implements
            // findObjectById — the sole engine method the provider needs.
            const hierarchyService = accessor.get(IHierarchyService);
            const propertyService = accessor.get(IPropertyService) as PropertyService;
            if (propertyService) {
                try {
                    const engineShim = {
                        findObjectById(id: string): unknown | null {
                            const node = hierarchyService?.findById(id);
                            return node?.owner ?? null;
                        },
                    };
                    propertyService.registerProvider(
                        instantiationService.createInstance<ThreePropertyProvider>(
                            new SyncDescriptor(ThreePropertyProvider, [engineShim])
                        )
                    );
                } catch (err) {
                    console.error("[Needle Inspector Agent] Failed to register ThreePropertyProvider:", err);
                }
            }

            await Promise.allSettled([
                userConfiguration.initialize()
            ]);

            resolve(application);
        });
    });
}


async function registerServices(): Promise<[InstantiationService, ServiceCollection, IUserConfiguration]> {
    const services = new ServiceCollection(...getSingletonServiceDescriptors());

    const productService = new ProductService({
        ...product,
        nameLong: "Needle Inspector Agent",
        nameShort: "Inspector Agent",
    });
    services.set(IProductService, productService);

    const instantiationService = new InstantiationService(services, false, undefined, false);
    services.set(IInstantiationService, instantiationService);

    // Logging
    const logLevel = LogLevel.Warning;
    const logsPath = URI.file(toLocalISOString(new Date()).replace(/-|:|\.\d+Z$/g, '')).with({ scheme: 'needle-log' });
    const fileLogger = new BufferLogger(logLevel);
    const fileService = new FileService(fileLogger);
    services.set(IFileService, fileService);
    const loggerService = new FileLoggerService(logLevel, logsPath, fileService);
    services.set(ILoggerService, loggerService);
    const otherLoggers: ILogger[] = [new ConsoleLogger(logLevel)];
    const logService = new LogService(fileLogger, otherLoggers);
    services.set(ILogService, logService);

    // Storage
    const storage = instantiationService.createInstance<LocalStorageFileSystemProvider>(new SyncDescriptor(LocalStorageFileSystemProvider));
    fileService.registerProvider('file', storage);

    const workspace: IAnyWorkspaceIdentifier = { id: window.location.host || 'agent' };
    const storageService = new BrowserStorageService(workspace, logService);
    try { await storageService.initialize(); }
    catch (error) { onUnexpectedError(error); }
    services.set(IStorageService, storageService);

    // Config
    services.set(IConfigurationService, new ConfigurationService());
    services.set(IContextKeyService, new SyncDescriptor(ContextKeyService));

    // Required by Application constructor
    services.set(IUndoRedoService, new SyncDescriptor(UndoRedoService));
    services.set(IKeyboardService, new SyncDescriptor(BrowserKeyboardService));
    services.set(IPointerService, new SyncDescriptor(BrowserPointerService));
    services.set(INotificationService, new SyncDescriptor(NotificationService));
    services.set(IContextMenuService, new SyncDescriptor(ContextMenuService));
    services.set(IMenuService, new SyncDescriptor(MenuService));
    services.set(IWindowsMainService, new SyncDescriptor(WindowsMainService, [{ allowPopout: false }]));
    services.set(IResourceService, new SyncDescriptor(ResourceService));

    // Analytics
    const rybbit = instantiationService.createInstance<RybbitAnalyticsService>(new SyncDescriptor(RybbitAnalyticsService, [{
        siteId: "a4c4c70027a1",
        trackErrors: true,
    }]));
    services.set(IAnalyticsService, rybbit);

    // No-op identity (agent has no login)
    services.set(IIdentitiyService, new NoopIdentityService() as any);

    // User config
    const config = instantiationService.createInstance<IUserConfiguration>(new SyncDescriptor(UserConfiguration));
    services.set(IUserConfiguration, config);

    // Properties
    services.set(IPropertyAccessorService, new SyncDescriptor(PropertyAccessorService));

    // WebGL
    services.set(IWebGLService, new SyncDescriptor(WebGLService));

    // Hierarchy
    services.set(IHierarchyService, new SyncDescriptor(HierarchyService));
    services.set(ITreeProviderService, new SyncDescriptor(TreeProviderService));
    services.set(IPropertyService, new SyncDescriptor(PropertyService));
    services.set(IProjectService, new SyncDescriptor(BasicProjectService));

    // AI tools
    const aiContext = new AIContextService();
    services.set(IAIContextService, aiContext);

    const toolService = instantiationService.createInstance<IAIToolService>(new SyncDescriptor(AIToolService));
    services.set(IAIToolService, toolService);

    const ai = instantiationService.createInstance<IAIService>(new SyncDescriptor(AIService));
    services.set(IAIService, ai);

    toolService.registerTool(instantiationService.createInstance<FindPropertiesTool>(new SyncDescriptor(FindPropertiesTool)));
    toolService.registerTool(instantiationService.createInstance<ReadPropertyTool>(new SyncDescriptor(ReadPropertyTool)));

    return [instantiationService, services, config];
}
